<template>

  <!-- Pricing Sections > Two tiers with extra tier -->
  <div class="isolate overflow-hidden bg-gray-900">
    <div class="mx-auto max-w-7xl px-6 pb-96 pt-24 text-center sm:pt-32 lg:px-8">
      <div class="mx-auto max-w-4xl">
        <h2 class="mx-auto max-w-4xl text-center text-5xl font-bold tracking-tight text-white">
          Podcast-Wachstum für alle
        </h2>
        <p class="mx-auto mt-4 max-w-2xl text-center text-lg leading-8 text-white/60">
          Mit St. Audio lernst du, wie du genauso viel Liebe in die Distribution deines Podcast stecken kannst,
          wie du es längst bei der Produktion tust. Dauerhaft kostenlos im Studio Basic oder mit allen
          interaktiven Funktionen im Studio Pro.
        </p>
      </div>
      <div class="mt-16 flex justify-center">
        <RadioGroup v-model="frequency" class="grid grid-cols-2 gap-x-1 rounded-full bg-white/5 p-1 text-center text-xs font-semibold leading-5 text-white">
          <RadioGroupLabel class="sr-only">
            Abo
          </RadioGroupLabel>
          <RadioGroupOption as="template" v-for="option in frequencies" :key="option.value" :value="option" v-slot="{ checked }">
            <div :class="[checked ? 'bg-teal-500' : '', 'cursor-pointer rounded-full py-1 px-2.5']">
              <span>
                {{ option.label }}
              </span>
            </div>
          </RadioGroupOption>
        </RadioGroup>
      </div>
    </div>
    <div class="flow-root bg-white pb-24 sm:pb-32">
      <div class="-mt-80">
        <div class="mx-auto max-w-7xl px-6 lg:px-8">
          <div class="mx-auto grid max-w-md grid-cols-1 gap-8 lg:max-w-4xl lg:grid-cols-2">
            <div v-for="tier in tiers" :key="tier.id" class="flex flex-col justify-between rounded-3xl bg-white p-8 shadow-xl ring-1 ring-gray-900/10 sm:p-10 mb-10">
              <div>
                <h3 v-if="tier.free" class="text-base font-semibold leading-7 text-teal-600">
                  Studio Basic
                </h3>
                <h3 v-else="tier.free" class="text-base font-semibold leading-7 text-teal-600">
                  Studio Pro
                </h3>
                <div v-if="tier.free" class="mt-4 flex items-baseline gap-x-2">
                  <span class="text-4xl font-bold tracking-tight text-gray-900">
                    Kostenlos
                  </span>
                </div>
                <div v-else="tier.free" class="mt-4 flex items-center gap-x-2">
                  <p v-if="monthly" class="text-gray-900 text-4xl font-bold tracking-tight">
                    {{ tier.monthly }}
                  </p>
                  <p v-if="annual" class="text-gray-900 text-4xl font-bold tracking-tight">
                    {{ tier.annual }}
                  </p>
                  <div class="text-sm leading-5">
                    <p :class="tier.promo ? 'text-gray-900' : 'text-white'">
                      EURO
                    </p>
                    <p :class="tier.promo ? 'text-gray-500' : 'text-gray-400'">
                      {{ frequency.details }}
                    </p>
                  </div>
                </div>
                <p class="mt-6 text-base leading-7 text-gray-600">
                  {{ tier.description }}
                </p>
                <ul role="list" class="mt-10 space-y-4 text-sm leading-6 text-gray-600">
                  <li v-for="feature in tier.spotlight" :key="feature" class="flex gap-x-3">
                    <CheckIcon class="h-6 w-5 flex-none text-teal-600" aria-hidden="true" />
                    {{ feature }}
                  </li>
                </ul>
              </div>
              <router-link :to="{name: 'setup'}" :aria-describedby="tier.id" class="bg-teal-700 shadow-sm hover:bg-teal-500 focus-visible:outline-teal-600 mt-8 rounded-md py-2 px-3 text-center text-sm font-semibold leading-6 text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2">
                Im Studio wachsen
              </router-link>
            </div>
            <div class="flex flex-col items-start gap-x-8 gap-y-6 rounded-3xl p-8 ring-1 ring-gray-900/10 sm:gap-y-10 sm:p-10 lg:col-span-2 lg:flex-row lg:items-center">
              <div class="lg:min-w-0 lg:flex-1">
                <h3 class="text-lg font-semibold leading-8 tracking-tight text-teal-600">
                  Noch unsicher, ob das St. Audio Studio zu dir passt?
                </h3>
                <p class="mt-1 text-base leading-7 text-gray-600">
                  Lass dir das Tool einfach vorab zeigen. Schnapp dir hier einen Slot, der dir gut passt.
                </p>
              </div>
              <a href="https://calendar.app.google/231ees5iGsb41R3BA" target="_blank" class="rounded-md px-3.5 py-2 text-sm font-semibold leading-6 text-teal-600 ring-1 ring-inset ring-teal-600 hover:ring-teal-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-400">
                Kalender öffnen <span aria-hidden="true">&rarr;</span>
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { RadioGroup } from '@headlessui/vue';
import { RadioGroupLabel } from '@headlessui/vue';
import { RadioGroupOption } from '@headlessui/vue';
import { CheckIcon } from '@heroicons/vue/20/solid';
import { useFetchResources } from '@/client/actions';
import { frequencies } from '@/static/enums';

const SPOTLIGHT_PLANS = ['basic', 'pro_one'];

const FREQUENCY_MONTHLY = 'monthly';
const FREQUENCY_ANNUAL = 'annual';

const frequency = ref(frequencies[0]);
const monthly = computed(() => frequency.value.value === FREQUENCY_MONTHLY);
const annual = computed(() => frequency.value.value === FREQUENCY_ANNUAL);

const target = computed(() => '/api/offerings');
const action = useFetchResources(target);

const { results, fetching, fetch, success, empty } = action;

const filter = (offering) => SPOTLIGHT_PLANS.includes(offering.tier);
const tiers = computed(() => results.value.filter(filter));

const mounted = onMounted(() => fetch());
const watcher = watch(target, (v, _) => fetch());
</script>
